import { ReactElement, useMemo } from 'react'

import { Select } from 'antd'
import classNames from 'classnames'
import { ErrorMessage, Field, Formik } from 'formik'
import { useTranslation } from 'react-i18next'
import { array, mixed, object, string } from 'yup'

import { fetchOrganizationSuccess } from 'actions/orgs'
import {
    createOrganization,
    NewOrganization,
} from 'api/Cerebro/orgScoped/organizationApi'
import { CerebroResourceResponse } from 'api/Cerebro/types'
import { Icon, Modal } from 'components/core'
import { FormikDatePicker, FormikInput, FormikSelect } from 'components/formik'
import { ToolTip } from 'components/ToolTip'
import { date, type Dayjs } from 'configuration/dayjs'
import { useOrgContext } from 'context/OrgContext'
import {
    PACIFIC_TIMEZONE,
    pacificCalendarDayStringToEndOfDayUtcIso,
} from 'helpers/dates'
import { useAction, useCerebroApiRequest } from 'hooks'
import { Organization } from 'types'
import message from 'utilities/message'

interface Props {
    visible: boolean
    closeModal: () => void
    isImpersonationMode?: boolean
}

interface CreateOrganizationFormValues {
    name: string
    salesforce_id: string
    trial_expiration_date?: Dayjs
    admin_invite_emails: string[]
}

const buildCreateOrganizationPayload = (
    values: CreateOrganizationFormValues,
    isImpersonationMode: boolean,
    isAgency: boolean
): NewOrganization => {
    const payload: NewOrganization = { name: values.name }
    if (!isImpersonationMode) {
        return payload
    }
    if (values.salesforce_id.trim()) {
        payload.salesforce_id = values.salesforce_id.trim()
    }
    if (values.trial_expiration_date) {
        // End of selected calendar day in Pacific (Cerebro validates in UTC).
        payload.trial_expiration_date =
            pacificCalendarDayStringToEndOfDayUtcIso(
                values.trial_expiration_date.format('YYYY-MM-DD')
            )
    } else if (!isAgency) {
        payload.trial_expiration_date = null
    }
    const adminInviteEmails = [
        ...new Set(
            values.admin_invite_emails
                .map((e) => e.trim().toLowerCase())
                .filter(Boolean)
        ),
    ]
    if (adminInviteEmails.length) {
        payload.admin_invite_emails = adminInviteEmails
    }
    return payload
}

const CreateOrganizationModal = ({
    visible,
    closeModal,
    isImpersonationMode = false,
}: Props): ReactElement => {
    const { t } = useTranslation(['common', 'navigation'])
    const { org: currentOrg } = useOrgContext()
    const makeCerebroApiRequest = useCerebroApiRequest()
    const mergeCreatedOrganization = useAction(fetchOrganizationSuccess)

    const defaultTrialExpirationDate = useMemo(() => {
        if (currentOrg?.is_agency === 1) {
            return undefined
        }
        return date().tz(PACIFIC_TIMEZONE).startOf('day').add(15, 'days')
    }, [currentOrg?.is_agency])

    const initialValues = useMemo(
        (): CreateOrganizationFormValues => ({
            name: '',
            salesforce_id: '',
            admin_invite_emails: [],
            ...(isImpersonationMode
                ? { trial_expiration_date: defaultTrialExpirationDate }
                : {}),
        }),
        [defaultTrialExpirationDate, isImpersonationMode]
    )

    const trialMinDatePacific = useMemo(
        () => date().tz(PACIFIC_TIMEZONE).startOf('day'),
        []
    )

    const validationSchema = useMemo(
        () =>
            object().shape(
                isImpersonationMode
                    ? {
                          name: string()
                              .label(
                                  t(
                                      'common:organizationName',
                                      'Organization Name'
                                  )
                              )
                              .required(),
                          salesforce_id: string(),
                          trial_expiration_date: mixed()
                              .optional()
                              .nullable()
                              .test(
                                  'trial-not-in-past-pacific',
                                  t(
                                      'common:organizationTrialExpirationDateInvalid',
                                      'Trial end date must be today or later (Pacific time).'
                                  ),
                                  (value) => {
                                      if (
                                          value == null ||
                                          !date.isDayjs(value)
                                      ) {
                                          return true
                                      }
                                      const selectedDay = value.startOf('day')
                                      const minPacific = date()
                                          .tz(PACIFIC_TIMEZONE)
                                          .startOf('day')
                                      return !selectedDay.isBefore(
                                          minPacific,
                                          'day'
                                      )
                                  }
                              ),
                          admin_invite_emails: array().of(
                              string().email(
                                  t(
                                      'common:organizationAdminInviteEmailInvalid',
                                      'Enter a valid email address'
                                  )
                              )
                          ),
                      }
                    : {
                          name: string()
                              .label(
                                  t(
                                      'common:organizationName',
                                      'Organization Name'
                                  )
                              )
                              .required(),
                      }
            ),
        [isImpersonationMode, t]
    )

    return (
        <Formik<CreateOrganizationFormValues>
            enableReinitialize
            initialValues={initialValues}
            onSubmit={async (values, { resetForm }) =>
                makeCerebroApiRequest<
                    Organization,
                    CerebroResourceResponse<Organization>
                >({
                    request: createOrganization(
                        buildCreateOrganizationPayload(
                            values,
                            isImpersonationMode,
                            currentOrg?.is_agency === 1
                        )
                    ),
                    onRequestSuccess: (response) => {
                        message.success(
                            t(
                                'navigation:SideMenu.CreateOrganizationModal.successMsg',
                                `Successfully create new organization named {{orgName}}.`,
                                { orgName: response.data.name }
                            )
                        )
                        resetForm()
                        closeModal()
                        mergeCreatedOrganization(response.data)
                    },
                })
            }
            validationSchema={validationSchema}
        >
            {({
                handleSubmit,
                resetForm,
                isSubmitting,
                values,
                errors,
                touched,
            }) => (
                <Modal
                    open={visible}
                    onCancel={() => {
                        resetForm()
                        closeModal()
                    }}
                    onOk={() => handleSubmit()}
                    okButtonProps={{
                        disabled: isSubmitting,
                        loading: isSubmitting,
                    }}
                    cancelButtonProps={{ disabled: isSubmitting }}
                    title={t(
                        'navigation:SideMenu.CreateOrganizationModal.title',
                        'Create a New Organization'
                    )}
                    okText={t(
                        'navigation:SideMenu.CreateOrganizationModal.okText',
                        'Create Organization'
                    )}
                >
                    <div>
                        <Field
                            component={FormikInput}
                            name="name"
                            placeholder={t(
                                'common:organizationName',
                                'Organization Name'
                            )}
                            size="large"
                        />
                        <ErrorMessage
                            name="name"
                            component="div"
                            className="fg-control-text is-error"
                        />
                    </div>
                    {isImpersonationMode && (
                        <>
                            <div style={{ marginTop: '10px' }}>
                                <Field
                                    component={FormikInput}
                                    name="salesforce_id"
                                    placeholder={t(
                                        'common:organizationSalesforceID',
                                        'Salesforce Customer ID'
                                    )}
                                    size="large"
                                />
                            </div>
                            <div
                                className="fg-form-control"
                                style={{ marginTop: '10px' }}
                            >
                                <div className="fg-control-label">
                                    {t(
                                        'common:organizationTrialExpirationDate',
                                        'Trial end date'
                                    )}{' '}
                                    <ToolTip
                                        title={t(
                                            'common:organizationTrialExpirationDateTooltip',
                                            'Date on which your trial will end at midnight Pacific Time.'
                                        )}
                                    >
                                        <Icon
                                            name="QUESTION_CIRCLE"
                                            color="textMuted"
                                            size={12}
                                        />
                                    </ToolTip>
                                </div>
                                <Field
                                    name="trial_expiration_date"
                                    component={FormikDatePicker}
                                    format="LL"
                                    minDate={trialMinDatePacific}
                                    size="middle"
                                    allowClear
                                />
                                <ErrorMessage
                                    name="trial_expiration_date"
                                    component="div"
                                    className="fg-control-text is-error"
                                />
                            </div>
                            <div
                                className="fg-form-control"
                                style={{ marginTop: '10px' }}
                            >
                                <div className="fg-control-label">
                                    {t(
                                        'common:organizationAdminInviteEmails',
                                        'Invite admins (email addresses)'
                                    )}
                                </div>
                                <Field
                                    name="admin_invite_emails"
                                    component={FormikSelect}
                                    mode="tags"
                                    optionFilterProp="children"
                                    parseLists
                                    placeholder={t(
                                        'common:organizationAdminInviteEmailsPlaceholder',
                                        'Type an email and press Enter'
                                    )}
                                    rootClassName={classNames({
                                        'fg-control-error':
                                            !!errors.admin_invite_emails &&
                                            touched.admin_invite_emails,
                                    })}
                                >
                                    {values.admin_invite_emails.map((email) => (
                                        <Select.Option
                                            key={email}
                                            value={email}
                                        >
                                            {email}
                                        </Select.Option>
                                    ))}
                                </Field>
                                <ErrorMessage
                                    name="admin_invite_emails"
                                    component="div"
                                    className="fg-control-text is-error"
                                />
                            </div>
                        </>
                    )}
                </Modal>
            )}
        </Formik>
    )
}

export default CreateOrganizationModal
